<template>
  <div class="week-calendar">
    <div v-if="employees.length > 1" class="week-calendar__employee">
      <v-select
        v-model="selectedEmployeeId"
        :items="employeeOptions"
        item-title="label"
        item-value="value"
        :label="$t('booking.provider')"
        density="comfortable"
        @update:model-value="loadAvailability"
      />
    </div>

    <div class="week-calendar__nav">
      <button
        type="button"
        class="week-calendar__nav-btn"
        :disabled="isCurrentWeek"
        @click="shiftWeek(-1)"
      >
        &lsaquo;
      </button>
      <div class="week-calendar__label">{{ weekLabel }}</div>
      <button type="button" class="week-calendar__nav-btn" @click="shiftWeek(1)">&rsaquo;</button>
    </div>

    <div class="week-calendar__day-tabs">
      <button
        v-for="(date, i) in weekDates"
        :key="date.toISOString()"
        type="button"
        class="week-calendar__day-tab"
        :class="{
          'week-calendar__day-tab--today': isToday(date),
          'week-calendar__day-tab--active': i === selectedDayIndex,
        }"
        @click="selectedDayIndex = i"
      >
        <span class="week-calendar__day-name">{{ isToday(date) ? $t("booking.today") : formatDayName(date) }}</span>
        <span class="week-calendar__day-date">{{ formatDayDate(date) }}</span>
      </button>
    </div>

    <div class="week-calendar__slots">
      <button
        v-for="slot in daySlots"
        :key="slot.time"
        type="button"
        class="week-calendar__slot"
        :class="{
          'week-calendar__slot--available': slot.state === 'available',
          'week-calendar__slot--booked': slot.state === 'booked',
          'week-calendar__slot--selected': slot.iso && slot.iso === modelValue,
        }"
        :disabled="slot.state !== 'available'"
        @click="slot.iso && selectSlot(slot.iso)"
      >
        {{ slot.state === "available" ? slot.time : $t("booking.booked") }}
      </button>

      <p v-if="loading" class="week-calendar__empty">{{ $t("booking.loading_availability") }}</p>
      <p v-else-if="!daySlots.length" class="week-calendar__empty">
        {{ $t("booking.no_working_hours") }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  getPublicAvailability,
  getPublicEmployees,
} from "../../services/public-booking-api.service";
import { PublicCompany, PublicEmployee } from "../../types/public-booking.type";

const { t } = useI18n();

const props = defineProps<{
  companyId: number | string;
  serviceId: number | null;
  timezone: string;
  modelValue: string | null;
  businessHours: PublicCompany["business_hours"];
  customSchedule: boolean | null;
  duration: number;
}>();

const emit = defineEmits<{
  "update:modelValue": [string | null];
  "update:employeeId": [number | null];
}>();

const slotsByDate = ref<Record<string, string[]>>({});
const employees = ref<PublicEmployee[]>([]);
const selectedEmployeeId = ref<number | null>(null);

// The chosen provider only ever mattered for filtering *availability* here -
// nothing surfaced it to the parent, so the actual booking was silently
// always created as "any provider" regardless of what was picked. Emitting
// it lets the parent pass the real selection through to the booking
// confirmation step.
watch(selectedEmployeeId, (id) => emit("update:employeeId", id), {
  immediate: true,
});
const loading = ref(false);
const weekStart = ref(startOfWeek(new Date()));
const selectedDayIndex = ref(0);

const employeeOptions = computed(() => [
  { label: t("booking.any_provider"), value: null },
  ...employees.value.map((e) => ({ label: e.name, value: e.id })),
]);

const weekDates = computed(() =>
  Array.from({ length: 7 }, (_, i) => addDays(weekStart.value, i))
);

const isCurrentWeek = computed(
  () => toDateStr(weekStart.value) === toDateStr(startOfWeek(new Date()))
);

const weekLabel = computed(() => {
  const start = weekDates.value[0];
  const end = weekDates.value[6];
  const startLabel = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const endLabel = end.toLocaleDateString(undefined, {
    month: start.getMonth() === end.getMonth() ? undefined : "short",
    day: "numeric",
  });
  return `${startLabel} – ${endLabel}`;
});

// A weekday can have more than one range (e.g. 9-13 and 14-18, to leave a
// break in between), so each weekday maps to a *list* of ranges rather than
// a single one.
const scheduleByWeekday = computed(() => {
  const map = new Map<number, { openMin: number; closeMin: number }[]>();
  // Companies on ad-hoc custom scheduling don't use the recurring weekly
  // `business_hours` at all — trusting it here would falsely mark days
  // "Booked" based on a schedule the backend never actually consults.
  if (props.customSchedule) return map;
  (props.businessHours || []).forEach((schedule) => {
    if (!schedule.opens_at || !schedule.closes_at) return;
    const ranges = map.get(schedule.weekday) || [];
    ranges.push({
      openMin: toMinutes(schedule.opens_at),
      closeMin: toMinutes(schedule.closes_at) - props.duration,
    });
    map.set(schedule.weekday, ranges);
  });
  return map;
});

type SlotState = "available" | "booked";

// Only the selected day's slots are rendered — each day keeps to its own
// business hours instead of every column being padded out to the longest
// day of the week, and there's nothing left over to lay out side by side,
// so the view never needs to scroll horizontally.
const daySlots = computed(() => {
  const date = weekDates.value[selectedDayIndex.value];
  const dateStr = toDateStr(date);
  const ranges = scheduleByWeekday.value.get(date.getDay()) || [];
  const available = new Map(
    (slotsByDate.value[dateStr] || []).map((iso) => [formatTimeKey(iso), iso])
  );

  const minutesSet = new Set<number>();
  ranges.forEach((range) => {
    for (let m = range.openMin; m <= range.closeMin; m += 15) minutesSet.add(m);
  });
  // Ad-hoc custom scheduling means `ranges` can be empty even though real
  // slots exist — always surface actual available times, or they'd never render.
  available.forEach((_, key) => minutesSet.add(toMinutes(key)));

  return Array.from(minutesSet)
    .sort((a, b) => a - b)
    .map((minutes) => {
      const time = minutesToLabel(minutes);
      const iso = available.get(time) || null;
      const withinAnyRange = ranges.some(
        (range) => minutes >= range.openMin && minutes <= range.closeMin
      );

      let state: SlotState | null = null;
      if (iso) {
        state = "available";
      } else if (withinAnyRange && !isPast(dateStr, time)) {
        state = "booked";
      }

      return state && { time, state, iso };
    })
    .filter((slot): slot is { time: string; state: SlotState; iso: string | null } => !!slot);
});

function toMinutes(hhmm: string) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function minutesToLabel(minutes: number) {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function isPast(dateStr: string, time: string) {
  return new Date(`${dateStr}T${time}:00`) < new Date();
}

function startOfWeek(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDateStr(date: Date) {
  // `toISOString` converts to UTC first, which silently rolls the date back
  // a day for any browser timezone ahead of UTC — read the local calendar
  // fields directly instead, since `date` is always built from local math.
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const formatTimeKey = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: props.timezone,
  }).format(new Date(iso));

const formatDayName = (date: Date) =>
  date.toLocaleDateString(undefined, { weekday: "short" });

const formatDayDate = (date: Date) =>
  date.toLocaleDateString(undefined, { day: "numeric", month: "short" });

const isToday = (date: Date) => toDateStr(date) === toDateStr(new Date());

const resetSelectedDay = () => {
  const todayIndex = weekDates.value.findIndex(isToday);
  selectedDayIndex.value = todayIndex >= 0 ? todayIndex : 0;
};

const shiftWeek = (weeks: number) => {
  weekStart.value = addDays(weekStart.value, weeks * 7);
  resetSelectedDay();
  emit("update:modelValue", null);
  loadAvailability();
};

const selectSlot = (iso: string) => {
  emit("update:modelValue", iso);
};

const loadAvailability = async () => {
  if (!props.serviceId) return;

  loading.value = true;
  try {
    const data = await getPublicAvailability(props.companyId, {
      service_id: props.serviceId,
      employee_id: selectedEmployeeId.value || undefined,
      start_date: toDateStr(weekDates.value[0]),
      end_date: toDateStr(weekDates.value[6]),
    });
    slotsByDate.value = data.slots_by_date;
  } finally {
    loading.value = false;
  }
};

const loadEmployees = async () => {
  if (!props.serviceId) return;
  employees.value = await getPublicEmployees(props.companyId, props.serviceId);

  // `loadEmployees` and the initial `loadAvailability` (see `onMounted`
  // below) both kick off in parallel, so that first availability fetch
  // always runs as "any provider" - it can't know the auto-selected/reset
  // employee_id below until this promise resolves. Re-running it here once
  // the real value is known keeps the slots shown consistent with whichever
  // employee's schedule actually applies (an employee's own schedule can
  // differ from the company's, e.g. someone taking a day off), instead of
  // silently leaving "any provider" slots on screen.
  if (employees.value.length === 1) {
    // Only one possible answer to "who's this with" - don't make the
    // customer pick it, the picker isn't even shown in this case (see
    // `v-if="employees.length > 1"` above).
    selectedEmployeeId.value = employees.value[0].id;
    loadAvailability();
  } else if (
    selectedEmployeeId.value !== null &&
    !employees.value.some((employee) => employee.id === selectedEmployeeId.value)
  ) {
    // A previous selection (for a different service) no longer applies.
    selectedEmployeeId.value = null;
    loadAvailability();
  }
};

watch(
  () => props.serviceId,
  () => {
    weekStart.value = startOfWeek(new Date());
    resetSelectedDay();
    emit("update:modelValue", null);
    loadEmployees();
    loadAvailability();
  }
);

onMounted(() => {
  resetSelectedDay();
  loadEmployees();
  loadAvailability();
});
</script>

<style lang="scss" scoped>
@use "../../styles/_breakpoints" as bp;

.week-calendar {
  &__employee {
    max-width: 280px;
    margin: 0 auto 16px;
  }

  &__nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 16px;
  }

  &__nav-btn {
    background: none;
    border: none;
    font-size: 22px;
    line-height: 1;
    color: #1f2937;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 8px;
    min-height: 44px;
    min-width: 44px;

    &:hover:not(:disabled) {
      background: #f3f4f6;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  &__label {
    font-weight: 600;
    font-size: 15px;
    color: #1f2937;
    min-width: 140px;
    text-align: center;
  }

  // A compact, horizontally-scrollable strip of day pills — this is the one
  // place a bit of horizontal scroll is fine (it's just navigation chips,
  // not content), and it's what lets the actual time-slot grid below stay a
  // single reflowing column set that never needs to scroll sideways.
  &__day-tabs {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding: 2px 2px 8px;
    margin-bottom: 4px;
    scroll-snap-type: x proximity;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__day-tab {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 52px;
    padding: 8px 10px;
    border: none;
    border-radius: 10px;
    background: #f3f4f6;
    cursor: pointer;
    scroll-snap-align: start;

    &--today {
      .week-calendar__day-name,
      .week-calendar__day-date {
        color: var(--booking-accent, #1976d2);
      }
    }

    &--active {
      background: var(--booking-accent, #1976d2);

      .week-calendar__day-name,
      .week-calendar__day-date {
        color: #fff;
      }
    }
  }

  &__day-name {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #6b7280;
  }

  &__day-date {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
  }

  // `auto-fill` reflows to however many columns actually fit the viewport —
  // unlike a fixed 7-column grid, it never needs a horizontal scrollbar.
  &__slots {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
    gap: 8px;
    max-height: min(55vh, 480px);
    overflow-y: auto;
    padding-bottom: 4px;

    @include bp.small-phone {
      grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
    }
  }

  &__slot {
    padding: 12px 6px;
    border-radius: 8px;
    border: none;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    min-height: 44px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--available {
      background: #e8f5e9;
      color: #2e7d32;
      cursor: pointer;

      &:hover {
        background: #c8e6c9;
      }
    }

    &--booked {
      background: #fdecea;
      color: #c0392b;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      cursor: not-allowed;
    }

    &--selected {
      background: var(--booking-accent, #1976d2) !important;
      color: #fff !important;
    }
  }

  &__empty {
    grid-column: 1 / -1;
    color: #888;
    font-size: 13px;
    text-align: center;
    padding: 16px 0;
  }
}
</style>
